import {
  PointerEvent as ReactPointerEvent,
  RefObject,
  useCallback,
  useState
} from "react";

import { PanelPosition, clampPanelPosition } from "stores/wellCardPanel";
import { effectivePanelWidth } from "stores/wellCardPanel/wellCardPanelStore";

export interface UsePanelDragParams {
  /** The card, read for its real size so the clamp is exact. */
  readonly panelRef: RefObject<HTMLElement>;
  /** A maximized card owns its geometry; dragging is off then. */
  readonly enabled: boolean;
  /**
   * The width the card is currently set to, so the clamp keeps the whole of a
   * widened card on screen rather than the default width's worth of it. What
   * is clamped against is what the card is DRAWN at, which is this width until
   * the window has no room for it.
   */
  readonly width: number;
  /**
   * The furthest left the card may reach, which is also what the width above
   * is capped against: the rest of the window is the application's chrome.
   */
  readonly leftBound: number;
  /**
   * Docked, the bar already has a gesture of its own - a click, which restores
   * the card where it was - so a drag has to be told apart from one. It arms on
   * pointer-down and starts only once the pointer has been lifted clear of the
   * bar. Undocked, the drag starts on the first move, as it always has.
   */
  readonly startsOnLift?: boolean;
  /** Run when a lifted drag starts, to bring back the card it takes over. */
  readonly onLift?: () => void;
  /**
   * Committed on release, not on every move: each write goes to the store,
   * which re-renders every reader of the card's position. The live position
   * above is what moves under the pointer.
   */
  readonly onCommit: (position: PanelPosition) => void;
}

export interface PanelDrag {
  /** Where the card is right now, while it is being dragged. */
  readonly livePosition: Maybe<PanelPosition>;
  readonly dragging: boolean;
  readonly onPointerDown: (event: ReactPointerEvent) => void;
}

/**
 * How far up out of the docked bar the pointer has to travel before the
 * gesture is a drag rather than a click. Far enough to outlast the shake in a
 * click, near enough that lifting the bar is one movement and not a knack.
 *
 * Upwards only: the bar sits on the bottom edge, so every position a drag can
 * reach is above it, and a sideways-only drag would have snapped the card up
 * to the clamp's floor without the user having asked it to go anywhere.
 */
const LIFT_THRESHOLD = 8;

/**
 * Drag the card by its title bar, docked or not.
 *
 * Pointer events, not mouse: one code path covers a mouse and a touch, and
 * `setPointerCapture` keeps the drag alive when the cursor outruns the bar,
 * which a fast drag always does. The capture is taken on the bar, and the bar
 * survives the card being restored under it, so a drag lifted out of the dock
 * carries on through the change of state.
 */
export function usePanelDrag({
  panelRef,
  enabled,
  width,
  leftBound,
  startsOnLift = false,
  onLift,
  onCommit
}: UsePanelDragParams): PanelDrag {
  const [livePosition, setLivePosition] = useState<Maybe<PanelPosition>>(null);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent) => {
      if (!enabled || event.button !== 0) return;
      const panel = panelRef.current;
      if (!panel) return;
      // A button in the bar is a control, not a handle.
      if ((event.target as HTMLElement).closest("button")) return;

      const box = panel.getBoundingClientRect();
      // Where in the card the pointer went down, so the card does not jump to
      // put its corner under the cursor. One pair covers both states: the
      // docked bar is the card's own width and its title bar's height, so an
      // offset measured in the bar still lands in the same place in the bar of
      // the card that comes back out of it.
      const grabX = event.clientX - box.left;
      const grabY = event.clientY - box.top;
      const liftFrom = event.clientY;

      const handle = event.currentTarget as HTMLElement;
      handle.setPointerCapture(event.pointerId);

      // Armed, but nothing has moved yet: until the pointer clears the bar the
      // gesture is still a click, and the card must not be touched.
      let started = !startsOnLift;

      // Read once, at the press: the card cannot be widened and the window
      // cannot be resized in the middle of a drag.
      const drawnWidth = effectivePanelWidth(width, leftBound);

      const positionAt = (pointer: PointerEvent) =>
        clampPanelPosition(
          {
            x: pointer.clientX - grabX,
            y: pointer.clientY - grabY
          },
          drawnWidth
        );

      const move = (moveEvent: PointerEvent) => {
        if (!started && liftFrom - moveEvent.clientY < LIFT_THRESHOLD) return;
        // The position is set BEFORE the card is brought back, so the restored
        // card renders once, already under the cursor and already in its
        // dragging state. The other order paints it at the position it was
        // last left in and eases across from there.
        setLivePosition(positionAt(moveEvent));
        if (!started) {
          started = true;
          onLift?.();
        }
      };

      const end = (endEvent: PointerEvent) => {
        handle.removeEventListener("pointermove", move);
        handle.removeEventListener("pointerup", end);
        handle.removeEventListener("pointercancel", end);
        // Released without ever leaving the bar. That is the bar's own click,
        // and committing a position here would move a card the user only
        // meant to bring back.
        if (!started) return;
        const settled = positionAt(endEvent);
        setLivePosition(null);
        onCommit(settled);
      };

      handle.addEventListener("pointermove", move);
      handle.addEventListener("pointerup", end);
      handle.addEventListener("pointercancel", end);
    },
    [enabled, panelRef, width, leftBound, startsOnLift, onLift, onCommit]
  );

  return { livePosition, dragging: livePosition != null, onPointerDown };
}
